import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { makeStyles, Button, IconButton, useMediaQuery } from "@material-ui/core";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ActionDialog from "../../common/ActionDialog";
import theme from "../../../themes";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../common/Loader/Loader";
import { ToastContainer } from "react-toastify";
import SuccessDialog from "../../common/SuccessDialog";
import { UserTypeContext } from "../../../UserTypeContext";
import { useLocation } from "react-router-dom";
import {
  BannerInput,
  BannerContentTypeEnum,
  CategoryAdvertisingBannerReturn,
  GetActiveCategoryAdvertisingBannersForChamberMemberDocument,
  GetUserQuery,
  useGetActiveCategoryAdvertisingBannersForChamberMemberQuery,
  UserType,
  useUpdateBannerContentForCategoryAdvertisingBannerMutation,
} from "../../../graphql/types";
import omitDeep from "omit-deep-lodash";
import { useGetCategoryIdToHumanReadableName } from "../../common/Hooks/useGetCategoryIdToHumanReadableName";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import BannerPickerAgreementOverlay from "../../common/BannerPickerAgreementOverlay";
import useHandleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement from "../../common/Hooks/useHandleAcceptanceOfChamberMemberCategoryAdvertisingBannerAgreement";
import ChamberMemberMemberProfileAndPerksBannersAgreementModal from "../../common/ChamberMemberMemberProfileAndPerksBannersAgreementModal";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf, openMemberInstructionPdf } from "../../common/openInstructionPdf";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ComingSoonDialog from "../../common/ComingSoonDialog";
import UploadRoundIcon from "../../../images/upload-rounded.png";
import EditOrangeIcon from "../../../images/edit-icon-orange.png";
import TrashIcon from "../../../images/trash.png";
import VideoIcon from "../../../images/lets-icons_video.png";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import BannerUploadModal from "../ChamberAdmin/BannerUploadModal";
import BannerPreviewModal from "../ChamberAdmin/BannerPreviewModal";
import DeleteBannerModal from "../ChamberAdmin/DeleteBannerModal";
import CategoryBannerPhoneMockup from "../../../images/cateogrybanner.jpeg";
import PreviewInactiveCategoryBanner from "../../../images/previewinactivecategorybanner.png";
import useCheckIfCategoryAdvertisingBannersActiveForChamberMember from "../../common/Hooks/useCheckIfCategoryAdvertisingBannersActiveForChamberMember.ts";
import useGetChamberMemberFromUserIdInAuth from "../../common/Hooks/useGetChamberMemberFromUserIdInAuth";

const useStyles = makeStyles((themes: any) => ({
  title: {
    marginLeft: "24px",
    marginRight: "24px",
    fontSize: "30px",
    color: "white",
    fontWeight: "bold",
    marginTop: "15px",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "12px",
      marginRight: "12px",
      fontSize: "22px",
    },
  },
  titleContent: {
    display: "flex",
    flexDirection: "column-reverse",
    backgroundColor: theme.palette.primary.main,
    overflowX: "hidden",
    [theme.breakpoints.up("lg")]: {
      paddingTop: "30px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "12px 0",
    },
  },
  pageHeaderButtonRow: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column" as const,
      alignItems: "stretch",
      gap: "8px",
      width: "100%",
      "& > div": { width: "100%", paddingLeft: "0 !important" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  content: {
    display: "flex",
    background: "#FFFFFF",
    minHeight: "80vh",
    paddingBottom: 30,
    borderRadius: "16px",
    marginTop: "33px",
    margin: "20px",
    flexDirection: "column",
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      margin: "0 8px",
      marginTop: 16,
    },
  },
  inactiveSection: {
    maxWidth: "1096px",
    width: "100%",
    margin: "50px auto",
    padding: "0px 30px 30px 30px",
    border: "1px solid #9E9E9E",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "36px",
    position: "relative" as const,
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      padding: "0px 12px 16px 12px",
      margin: "20px auto",
    },
  },
  sectionHeaderBlack: {
    backgroundColor: "#000000",
    borderRadius: "30px",
    padding: "17px 18px",
    display: "inline-block",
    alignSelf: "flex-start",
    position: "relative" as const,
    top: "-30px",
    marginBottom: "-30px",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 16px",
      top: "-20px",
      marginBottom: "-12px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 700,
    fontFamily: "DM Sans, sans-serif",
    lineHeight: "24px",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      lineHeight: "18px",
    },
  },
  inactiveDescText: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "center" as const,
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
  },
  clickHereLink: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    cursor: "pointer",
    fontWeight: 700,
  },
  bannersGrid: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "14px",
    width: "100%",
    overflowX: "auto",
  },
  bannersLabelsRow: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "12px",
  },
  bannersCardsRow: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap" as const,
    justifyContent: "center" as const,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column" as const,
    },
  },
  bannerSlotDisabled: {
    width: "158px",
    height: "108px",
    backgroundColor: "#9E9E9E",
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "22px 12px",
    gap: "10px",
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  bannerSlotDisabledIcon: {
    width: "36px",
    height: "36px",
    filter: "brightness(0) invert(1)",
  },
  bannerSlotDisabledText: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "24px",
    color: "#FFFFFF",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
  },
  bannerLabel: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#888888",
    textAlign: "center" as const,
    lineHeight: "24px",
    width: "158px",
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  previewBannersButton: {
    backgroundColor: "#888888",
    color: "#FFFFFF",
    borderRadius: "12px",
    padding: "15px 40px",
    fontSize: "20px",
    fontWeight: 700,
    fontFamily: "DM Sans, sans-serif",
    lineHeight: "150%",
    textTransform: "uppercase" as const,
    cursor: "not-allowed",
    "&:hover": { backgroundColor: "#888888" },
    "&.Mui-disabled": {
      backgroundColor: "#888888",
      color: "#FFFFFF",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      fontSize: "16px",
      padding: "12px 20px",
    },
  },
  inactiveUploadDesc: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "center" as const,
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
  },
  inactiveQualityDesc: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "center" as const,
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
  },
  previewImage: {
    maxWidth: "100%",
    width: "700px",
    height: "auto",
    objectFit: "contain" as const,
  },
  activeSection: {
    maxWidth: "1096px",
    width: "100%",
    margin: "30px auto",
    padding: "0px 30px 30px 30px",
    border: "1px solid #9E9E9E",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    position: "relative" as const,
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      padding: "0px 12px 16px 12px",
      margin: "16px auto",
    },
  },
  sectionHeaderGold: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "30px",
    padding: "17px 18px",
    display: "inline-block",
    alignSelf: "flex-start",
    position: "relative" as const,
    top: "-30px",
    marginBottom: "-16px",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 16px",
      top: "-20px",
      marginBottom: "-8px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  activeDescText: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "left" as const,
    alignSelf: "stretch" as const,
    width: "100%",
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  activeQualityDesc: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "left" as const,
    alignSelf: "stretch" as const,
    width: "100%",
    margin: "10px 0 0 0",
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
}));

type CustomCategoryAdvertisingBannerReturn = CategoryAdvertisingBannerReturn & {
  humanReadableCategoryName: string;
};

const UniqueBannerPicker = ({
  categoryAdvertisingBanner,
  chamberMemberData,
}: {
  categoryAdvertisingBanner: CustomCategoryAdvertisingBannerReturn;
  chamberMemberData:
    | GetUserQuery["getUser"]["chamberMemberInfo"]
    | null
    | undefined;
}): ReactElement => {
  const [bannerUpdateInProgress, setBannerUpdateInProgress] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [activeBannerIndex, setActiveBannerIndex] = useState(1);
  const [bannerPreviewOpen, setBannerPreviewOpen] = useState(false);
  const [deleteBannerIndex, setDeleteBannerIndex] = useState<number | null>(null);

  const [updateBannerContentForCategoryAdvertisingBanner] =
    useUpdateBannerContentForCategoryAdvertisingBannerMutation();

  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");

  // Banner data: map existing banners to 4 slots
  const getBannersData = useCallback(() => {
    const existingBanner = categoryAdvertisingBanner?.bannerSettings?.activationInfo?.banner;
    const emptyBanner = { image: null as string | null, videoUrl: null as string | null, redirectUrl: null as string | null, waitingForApproval: false };
    if (existingBanner && existingBanner.length > 0) {
      const mapped = existingBanner.map((b: any) => ({
        image: b?.contentType === "IMAGE" ? b?.contentUrl || null : null,
        videoUrl: b?.contentType === "VIDEO" ? b?.contentUrl || null : null,
        redirectUrl: b?.imageLink || null,
        waitingForApproval: b?.waitingForApprovalByChamberAdmin ?? false,
      }));
      while (mapped.length < 4) mapped.push({ ...emptyBanner });
      return mapped;
    }
    return [{ ...emptyBanner }, { ...emptyBanner }, { ...emptyBanner }, { ...emptyBanner }];
  }, [categoryAdvertisingBanner?.bannerSettings?.activationInfo?.banner]);

  const [bannersData, setBannersData] = useState(getBannersData());

  useEffect(() => {
    setBannersData(getBannersData());
  }, [getBannersData]);

  const handleBannerUpdate = useCallback(
    async (
      updatedBanner: BannerInput[],
      isSlidePositioningUpdate?: boolean
    ) => {
      setBannerUpdateInProgress(true);

      await updateBannerContentForCategoryAdvertisingBanner({
        variables: {
          categoryId: categoryAdvertisingBanner?.categoryId || "",
          banner: omitDeep(updatedBanner, [
            "__typename",
            "waitingForApprovalByChamberAdmin",
          ]) as BannerInput[],
        },
        refetchQueries: [
          {
            query: GetActiveCategoryAdvertisingBannersForChamberMemberDocument,
          },
        ],
        awaitRefetchQueries: true,
      });

      setBannerUpdateInProgress(false);

      const isAutoApproveOrReposition =
        isSlidePositioningUpdate ||
        categoryAdvertisingBanner?.bannerSettings?.activationInfo
          ?.autoApproveBannerContentUpdate;

      const msg = isAutoApproveOrReposition
        ? enumStrings.chamberMember.categoryBannerUpdatedSuccessfully.replace(
            "{bannerNumber}",
            categoryAdvertisingBanner.humanReadableCategoryName
          )
        : enumStrings.chamberMember.categoryBannerSentForApproval.replace(
            "{bannerNumber}",
            categoryAdvertisingBanner.humanReadableCategoryName
          );
      setSuccessMessage(msg);
      setShowSuccessDialog(true);
    },
    [
      categoryAdvertisingBanner?.bannerSettings?.activationInfo
        ?.autoApproveBannerContentUpdate,
      categoryAdvertisingBanner?.categoryId,
      categoryAdvertisingBanner.humanReadableCategoryName,
      enumStrings.chamberMember.categoryBannerSentForApproval,
      enumStrings.chamberMember.categoryBannerUpdatedSuccessfully,
      updateBannerContentForCategoryAdvertisingBanner,
    ]
  );

  const handleOpenBannerModal = (index: number) => {
    setActiveBannerIndex(index);
    setBannerModalOpen(true);
  };

  const handleSaveBanner = useCallback((data: { image: string | null; videoUrl: string | null; redirectUrl: string | null; }) => {
    const newBannersData = [...bannersData];
    newBannersData[activeBannerIndex - 1] = {
      ...data,
      waitingForApproval: bannersData[activeBannerIndex - 1]?.waitingForApproval ?? false,
    };
    setBannersData(newBannersData);
    setBannerModalOpen(false);

    const bannerInputs: BannerInput[] = newBannersData
      .filter(b => b.image || b.videoUrl)
      .map((b, i) => ({
        contentUrl: b.image || b.videoUrl || "",
        imageLink: b.redirectUrl || undefined,
        contentType: b.videoUrl ? BannerContentTypeEnum.Video : BannerContentTypeEnum.Image,
        position: i,
      }));
    handleBannerUpdate(bannerInputs);
  }, [bannersData, activeBannerIndex, handleBannerUpdate]);

  const handleMoveBanner = (index: number, direction: 'left' | 'right') => {
    const newBannersData = [...bannersData];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newBannersData.length) {
      const temp = newBannersData[index];
      newBannersData[index] = newBannersData[targetIndex];
      newBannersData[targetIndex] = temp;
      setBannersData(newBannersData);

      const bannerInputs: BannerInput[] = newBannersData
        .filter(b => b.image || b.videoUrl)
        .map((b, i) => ({
          contentUrl: b.image || b.videoUrl || "",
          imageLink: b.redirectUrl || undefined,
          contentType: b.videoUrl ? BannerContentTypeEnum.Video : BannerContentTypeEnum.Image,
          position: i,
        }));
      handleBannerUpdate(bannerInputs, true);
    }
  };

  const handleDeleteBanner = (index: number) => {
    const newBannersData = [...bannersData];
    newBannersData[index] = { image: null, videoUrl: null, redirectUrl: null, waitingForApproval: false };
    setBannersData(newBannersData);

    const bannerInputs: BannerInput[] = newBannersData
      .filter(b => b.image || b.videoUrl)
      .map((b, i) => ({
        contentUrl: b.image || b.videoUrl || "",
        imageLink: b.redirectUrl || undefined,
        contentType: b.videoUrl ? BannerContentTypeEnum.Video : BannerContentTypeEnum.Image,
        position: i,
      }));
    handleBannerUpdate(bannerInputs);
  };

  const activeBanners = bannersData.filter(b => b.image || b.videoUrl);

  const chamberInfo = useGetChamberFromUserIdInAuth();

  const {
    chamberMembeCategoryAdvertisingBannerAgreementIsAccepted,
    handleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement,
  } = useHandleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement({
    categoryId: categoryAdvertisingBanner.categoryId,
    chamberMemberHasAcceptedAgreement:
      categoryAdvertisingBanner?.bannerSettings?.activationInfo
        ?.chamberMemberHasAcceptedAgreement,
  });

  const [
    isCategoryAdvertisingBannerAgreementModalOpen,
    setIsCategoryAdvertisingBannerAgreementModalOpen,
  ] = useState(false);

  const openChamberMemberCategoryAdvertisingBannerAgreementModal =
    useCallback(() => {
      setIsCategoryAdvertisingBannerAgreementModalOpen(true);
    }, []);

  return (
    <div className={classes.activeSection}>
      <div className={classes.sectionHeaderGold}>
        <p className={classes.sectionTitle}>
          {enumStrings.chamberMember.categoryAdvertisingActiveBannerTitle.replace(
            "{categoryName}",
            categoryAdvertisingBanner.humanReadableCategoryName
          )}
        </p>
      </div>

      <p className={classes.activeDescText}>
        {enumStrings.chamberMember.categoryAdvertisingActiveApprovalDesc.replace(
          "{chamberName}",
          chamberInfo?.name || ""
        )}
      </p>

      <p className={classes.activeDescText} style={{ marginTop: "10px" }}>
        {enumStrings.chamberMember.categoryAdvertisingBannerUploadDesc}
      </p>
      <p className={classes.activeQualityDesc} dangerouslySetInnerHTML={{
        __html: enumStrings.chamberMember.categoryAdvertisingBannerQualityDesc
      }} />

      <div style={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <ChamberMemberMemberProfileAndPerksBannersAgreementModal
          open={isCategoryAdvertisingBannerAgreementModalOpen}
          handleClose={() =>
            setIsCategoryAdvertisingBannerAgreementModalOpen(false)
          }
          handleAcceptanceOfChamberMemberMemberProfileAndPerksBannersAgreement={
            handleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement
          }
          chamberName={chamberInfo?.name || ""}
          isCategoryBanner
        />
        <BannerPickerAgreementOverlay
          chamberMemberMemberProfileAndPerksBannersAgreementIsAccepted={
            chamberMembeCategoryAdvertisingBannerAgreementIsAccepted
          }
          openChamberMemberMemberProfileAndPerksBannersAgreementModal={
            openChamberMemberCategoryAdvertisingBannerAgreementModal
          }
          paddingLeftPx={30}
          userType={UserType.ChamberMemberAdmin}
        />

        {bannerUpdateInProgress && (
          <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
            <Loader size={80} fullPage={false} />
          </div>
        )}

        {/* Banner Upload Grid - All 4 in one row (2-col grid on mobile) */}
        <div
          style={isMobile ? {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
            marginTop: "16px",
            marginBottom: "14px",
            width: "100%",
          } : {
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: "20px",
            marginTop: "20px",
            justifyContent: "center",
            marginBottom: "14px",
          }}
        >
          {bannersData.map((banner, index) => {
            const hasBanner = banner.image || banner.videoUrl;
            const isApproved = hasBanner ? !banner.waitingForApproval : false;
            return (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: isMobile ? "8px" : "12px",
                width: isMobile ? "100%" : "auto",
                minWidth: 0,
              }}
            >
              <p
                style={{
                  textAlign: "center",
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  fontSize: isMobile ? "15px" : "20px",
                  lineHeight: isMobile ? "20px" : "24px",
                  margin: 0,
                }}
              >
                {enumStrings.manageChamberEngage.categoryAdvertisingBanners.bannerLabel.replace("{{number}}", String(index + 1))}
              </p>
              {banner.image || banner.videoUrl ? (
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    width: isMobile ? "100%" : "209px",
                    height: isMobile ? "150px" : "175px",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      position: "relative",
                      padding: "12px",
                      flex: 1,
                    }}
                  >
                    {banner.image ? (
                      <img
                        src={banner.image}
                        alt={`Banner ${index + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          fontSize: "20px",
                          fontWeight: 600,
                          gap: "8px",
                          border: `1px solid ${theme.palette.primary.main}`,
                          borderRadius: "4px",
                          boxSizing: "border-box",
                        }}
                      >
                        <img src={VideoIcon} alt="Video" style={{ width: "49px", height: "49px" }} />
                        {enumStrings.editMemberForm?.watchVideo || "Watch Video"}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 12px",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenBannerModal(index + 1)}
                        style={{
                          backgroundColor: "#E3E3E3",
                          borderRadius: "5px",
                          width: "36px",
                          height: "33px",
                          padding: "4px",
                        }}
                      >
                        <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px", filter: "brightness(0)" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteBannerIndex(index)}
                        style={{
                          backgroundColor: (theme.palette as any).delete.main,
                          borderRadius: "5px",
                          width: "36px",
                          height: "33px",
                          padding: "4px",
                        }}
                      >
                        <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "22px", filter: "brightness(0) invert(1)" }} />
                      </IconButton>
                    </div>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveBanner(index, 'left')}
                        disabled={index === 0}
                        style={{
                          backgroundColor: index === 0 ? "#D9D9D9" : theme.palette.primary.main,
                          color: index === 0 ? "#666" : "white",
                          borderRadius: "5px",
                          minWidth: "29px",
                          width: "29px",
                          height: "26px",
                          padding: "4px",
                        }}
                      >
                        <ArrowBackIosIcon style={{ fontSize: "12px", marginLeft: "4px" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveBanner(index, 'right')}
                        disabled={index === bannersData.length - 1}
                        style={{
                          backgroundColor: index === bannersData.length - 1 ? "#D9D9D9" : theme.palette.primary.main,
                          color: index === bannersData.length - 1 ? "#666" : "white",
                          borderRadius: "5px",
                          minWidth: "29px",
                          width: "29px",
                          height: "26px",
                          padding: "4px",
                        }}
                      >
                        <ArrowForwardIosIcon style={{ fontSize: "12px" }} />
                      </IconButton>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => handleOpenBannerModal(index + 1)}
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    width: isMobile ? "100%" : "182px",
                    minWidth: isMobile ? 0 : "120px",
                    height: isMobile ? "90px" : "114px",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    padding: isMobile ? "12px 8px" : "22px 12px",
                    gap: isMobile ? "6px" : "10px",
                  }}
                >
                  <img
                    src={UploadRoundIcon}
                    alt="Upload"
                    style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px" }}
                  />
                  <p
                    style={{
                      color: theme.palette.primary.main,
                      fontSize: isMobile ? "12px" : "20px",
                      fontWeight: 600,
                      margin: 0,
                      lineHeight: isMobile ? "1.3" : "24px",
                      textAlign: "center",
                      whiteSpace: isMobile ? "normal" : "nowrap",
                    }}
                  >
                    {enumStrings.manageChamberEngage.categoryAdvertisingBanners.uploadBanner}
                  </p>
                </div>
              )}
              {hasBanner && (
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "24px",
                    fontStyle: "italic",
                    color: isApproved ? theme.palette.success.main : theme.palette.primary.main,
                    marginTop: "4px",
                  }}
                >
                  {isApproved ? enumStrings.approved : enumStrings.waitingForApproval}
                </span>
              )}
            </div>
            );
          })}
        </div>

        {/* Preview Banners Button */}
        {activeBanners.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "10px", marginBottom: "20px" }}>
            <Button
              variant="contained"
              onClick={() => setBannerPreviewOpen(true)}
              style={{
                backgroundColor: "#333",
                color: "white",
                fontSize: "16px",
                fontWeight: "bold",
                borderRadius: "25px",
                padding: "12px 40px",
                textTransform: "none",
              }}
            >
              {enumStrings.createMemberForm.previewBannersButton}
            </Button>
          </div>
        )}

        <BannerPreviewModal
          open={bannerPreviewOpen}
          onClose={() => setBannerPreviewOpen(false)}
          banners={activeBanners}
          phoneMockupImage={CategoryBannerPhoneMockup}
        />

        <BannerUploadModal
          open={bannerModalOpen}
          onClose={() => setBannerModalOpen(false)}
          bannerIndex={activeBannerIndex}
          initialData={bannersData[activeBannerIndex - 1] || { image: null, videoUrl: null, redirectUrl: null }}
          onSave={handleSaveBanner}
        />

        <DeleteBannerModal
          open={deleteBannerIndex !== null}
          onClose={() => setDeleteBannerIndex(null)}
          onDelete={() => {
            if (deleteBannerIndex !== null) {
              handleDeleteBanner(deleteBannerIndex);
              setDeleteBannerIndex(null);
            }
          }}
        />
      </div>
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        autoDismissMs={15000}
      />
    </div>
  );
};

const ManageAllCategoryAdvertisingBannersForChammberCategoryAdvertisingBannersChamberMember =
  (): ReactElement => {
    const classes = useStyles();
    const enumStrings = useEnumStrings();

    const {
      state: { userType },
    } = useContext(UserTypeContext);

    const location = useLocation();

    const rootAdminObj = // @ts-ignore
      enumStrings?.groups[userType][
        RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember
      ];

    const [isLoading, setIsLoading] = useState(true);
    const [contactChamberOpen, setContactChamberOpen] = useState(false);
    const [showComingSoon, setShowComingSoon] = useState(false);

    const { data: getActiveCategoryAdvertisingBannersForChamberMember } =
      useGetActiveCategoryAdvertisingBannersForChamberMemberQuery();

    const [enabledCatBanner, setEnabledCatBanner] = useState<
      CustomCategoryAdvertisingBannerReturn[]
    >([]);

    const { categoryIdToHumanReadableName } =
      useGetCategoryIdToHumanReadableName();

    const chamberMemberData = useGetChamberMemberFromUserIdInAuth();

    useEffect(() => {
      if (
        !getActiveCategoryAdvertisingBannersForChamberMember ||
        !categoryIdToHumanReadableName
      ) {
        return;
      }

      const res = [];
      for (const catBanner of getActiveCategoryAdvertisingBannersForChamberMember?.getActiveCategoryAdvertisingBannersForChamberMember ||
        []) {
        res.push({
          ...catBanner,
          humanReadableCategoryName:
            categoryIdToHumanReadableName[catBanner.categoryId],
        });
      }

      setEnabledCatBanner(res as CustomCategoryAdvertisingBannerReturn[]);

      setIsLoading(false);
    }, [
      categoryIdToHumanReadableName,
      getActiveCategoryAdvertisingBannersForChamberMember,
    ]);

    const chamberInfo = useGetChamberFromUserIdInAuth();

    const isCategoryAdvertisingBannersActiveForChamberMember =
      useCheckIfCategoryAdvertisingBannersActiveForChamberMember({
        userType,
      });

    return (
      <div style={{ overflowX: "hidden" }}>
        <div className={classes.titleContent}>
          <div
            className={classes.title}
            style={{
              flexDirection: "column",
            }}
          >
            <span>
              {rootAdminObj.title}
              {(location?.state as any)?.chamberName &&
                ` for ${(location?.state as any)?.chamberName}`}
            </span>
            <span
              style={{
                marginLeft: "1px",
                fontWeight: "normal",
                fontSize: "16px",
                lineHeight: "1.55",
              }}
            >
              {rootAdminObj.description} {enumStrings.watchVideoToLearnHow}
            </span>
            <div style={{ height: 10 }} />
            <div
              className={classes.pageHeaderButtonRow}
              style={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: "row",
              }}
            >
              <div style={{ paddingBottom: "10px" }}>
                <VideoTutorialButton
                  tutorialUrl="https://share.synthesia.io/embeds/videos/fe255c22-da44-440d-b8d4-c175bc287a23"
                />
              </div>
              <div style={{ paddingBottom: "10px", paddingLeft: "10px" }}>
                <ExportButton
                  customOnClick={() =>
                    userType === UserType.ChamberMemberAdmin
                      ? openMemberInstructionPdf(
                          isCategoryAdvertisingBannersActiveForChamberMember
                            ? "Member Dashboard - Category Banners  ACTIVE.pdf"
                            : "Member Dashboard - Category Banners  INACTIVE.pdf"
                        )
                      : openInstructionPdf("Activate Category Member Advertising Banner.pdf")
                  }
                  disablePadding
                  icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
                  label={enumStrings.manageChamberEngage.viewInstructions}
                />
              </div>
            </div>
          </div>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
          />
        </div>
        <div className={classes.content}>
          {isLoading ? (
            <Loader size={80} fullPage={false} />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                marginTop: 40,
              }}
            >
              {isCategoryAdvertisingBannersActiveForChamberMember ? (
                enabledCatBanner.map((catBanner) => (
                  <UniqueBannerPicker
                    key={catBanner.categoryId}
                    categoryAdvertisingBanner={catBanner}
                    chamberMemberData={chamberMemberData}
                  />
                ))
              ) : (
                <div className={classes.inactiveSection}>
                  <div className={classes.sectionHeaderBlack}>
                    <p className={classes.sectionTitle}>
                      {enumStrings.chamberMember.categoryAdvertisingNotActivatedTitle.replace("{categoryName}", "{Category Name}")}
                    </p>
                  </div>

                  <p className={classes.inactiveDescText}>
                    <span dangerouslySetInnerHTML={{
                      __html: enumStrings.chamberMember.categoryAdvertisingNotActivatedDesc
                        .split("{clickHere}")[0]
                    }} />
                    <span
                      className={classes.clickHereLink}
                      onClick={() => setContactChamberOpen(true)}
                    >
                      {enumStrings.createMemberForm.clickHereLink}
                    </span>
                    {enumStrings.chamberMember.categoryAdvertisingNotActivatedDesc
                      .split("{clickHere}")[1]}
                  </p>

                  <div style={{ textAlign: "center" }}>
                    <p className={classes.inactiveUploadDesc}>
                      {enumStrings.chamberMember.categoryAdvertisingBannerUploadDesc}
                    </p>
                    <p className={classes.inactiveQualityDesc} dangerouslySetInnerHTML={{
                      __html: enumStrings.chamberMember.categoryAdvertisingBannerQualityDesc
                    }} />
                  </div>

                  <img
                    src={PreviewInactiveCategoryBanner}
                    alt="Category Banner Preview"
                    className={classes.previewImage}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <ComingSoonDialog
          open={showComingSoon}
          onClose={() => setShowComingSoon(false)}
        />

        {/* Contact Chamber Dialog */}
        <ActionDialog
          open={contactChamberOpen}
          onClose={() => setContactChamberOpen(false)}
          icon={<MailOutlineIcon style={{ color: "#FFFFFF", fontSize: "40px" }} />}
          iconBgColor="#4CAF50"
          title={enumStrings.createMemberForm.contactChamberTitle}
          description={enumStrings.createMemberForm.contactChamberDesc}
          buttonText={enumStrings.createMemberForm.contactChamberSendRequest}
          onButtonClick={() => {
            const memberName = chamberMemberData?.name || "";
            const chamberName = chamberInfo?.name || "";
            const toEmail = chamberInfo?.supportContactEmail || "";
            const subject = encodeURIComponent(
                enumStrings.createMemberForm.contactChamberEmailSubject.replace("{memberName}", memberName)
            );
            const body = encodeURIComponent(
                enumStrings.createMemberForm.contactChamberEmailBody
                    .replace("{chamberName}", chamberName)
                    .replace("{memberName}", memberName)
            );
            window.open(`mailto:${toEmail}?subject=${subject}&body=${body}`, "_blank");
            setContactChamberOpen(false);
          }}
        />
      </div>
    );
  };

export default ManageAllCategoryAdvertisingBannersForChammberCategoryAdvertisingBannersChamberMember;
